import { useCallback, useMemo, useState } from "react"
import moment from "moment"
import { useCalendarJournalNoteDates } from "../../../hooks/useCalendarSelectors"
import { useTimeOffByDay } from "../../../hooks/useTimeOff"
import { useRefreshCalendarBookings } from "../../../useRefreshCalendarBookings"
import { useDevice } from "hooks/useDevice"
import useActiveGuide from "hooks/useActiveClient"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import { computeEventColumns, getLanesForDay, getDayMinWidth, HOUR_HEIGHT } from "../timeGridUtils"

const MAX_VISIBLE = 2

const normalizeEvent = (ev) => {
  const startMoment = ev.start ? moment(ev.start, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null
  const endMoment = ev.end ? moment(ev.end, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null
  return {
    ...ev,
    startMoment,
    endMoment,
    startLabel: startMoment?.isValid() ? startMoment.format("h:mm A") : ev.start,
    endLabel: endMoment?.isValid() ? endMoment.format("h:mm A") : ev.end,
  }
}

const useWeekGridData = ({ calendarDays, events, hours, agenda, startHourProp, endHourProp }) => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)
  const [selectedBookingId, setSelectedBookingId] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [isJournalModalOpen, setIsJournalModalOpen] = useState(false)
  const [visibleCounts, setVisibleCounts] = useState({})

  const { isMobile, isTablet } = useDevice()
  const { isActive: active } = useActiveGuide()
  const { hasPermission } = usePermissions()
  const journalNoteDates = useCalendarJournalNoteDates()
  const timeOffByDay = useTimeOffByDay(calendarDays)
  const refreshCalendarBookings = useRefreshCalendarBookings()

  const isCompact = isMobile || isTablet
  const canViewJournal = hasPermission(MODULES.CALENDAR, ACTIONS.VIEW_JOURNAL)
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL)
  const canEditJournal = hasPermission(MODULES.CALENDAR, ACTIONS.EDIT_JOURNAL)

  const startHour = hours[0] ?? 0
  const totalHeight = hours.length * HOUR_HEIGHT
  const filterStart = startHourProp ?? startHour
  const filterEnd = endHourProp ?? (hours[hours.length - 1] ?? 23)

  const getVisibleCount = useCallback((key) => visibleCounts[key] ?? MAX_VISIBLE, [visibleCounts])
  const showMore = useCallback((key) => setVisibleCounts((prev) => ({ ...prev, [key]: (prev[key] ?? MAX_VISIBLE) + MAX_VISIBLE })), [])
  const showLess = useCallback((key) => setVisibleCounts((prev) => ({ ...prev, [key]: MAX_VISIBLE })), [])

  const normalizedEventsByDay = useMemo(() => calendarDays.reduce((acc, day) => {
    const dayStr = day.format("YYYY-MM-DD")
    acc[dayStr] = events
      .filter((ev) => {
        if (!moment(ev.date).isSame(day, "day")) return false
        if (!agenda) return true
        const sm = ev.start ? moment(ev.start, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null
        if (!sm?.isValid()) return true
        return sm.hour() >= filterStart && sm.hour() < filterEnd
      })
      .map(normalizeEvent)
    return acc
  }, {}), [calendarDays, events, agenda, filterStart, filterEnd])

  const eventsByDayWithCols = useMemo(() => calendarDays.reduce((acc, day) => {
    const dayStr = day.format("YYYY-MM-DD")
    const valid = (normalizedEventsByDay[dayStr] || []).filter((ev) => ev.startMoment?.isValid() && ev.endMoment?.isValid())
    acc[dayStr] = computeEventColumns(valid)
    return acc
  }, {}), [calendarDays, normalizedEventsByDay])

  const gridTemplateColumns = useMemo(() => {
    const cols = calendarDays.map((day) => {
      const lanes = getLanesForDay(eventsByDayWithCols[day.format("YYYY-MM-DD")] || [])
      return `minmax(${getDayMinWidth(lanes)}px, 1fr)`
    })
    return agenda ? `80px ${cols.join(" ")}` : cols.join(" ")
  }, [calendarDays, eventsByDayWithCols, agenda])

  const gridMinWidth = useMemo(() => {
    const sum = calendarDays.reduce((acc, day) => {
      const lanes = getLanesForDay(eventsByDayWithCols[day.format("YYYY-MM-DD")] || [])
      return acc + getDayMinWidth(lanes)
    }, 0)
    return agenda ? 80 + sum : sum
  }, [calendarDays, eventsByDayWithCols, agenda])

  const isWeekEmpty = calendarDays.every((day) => {
    const dayStr = day.format("YYYY-MM-DD")
    return !(normalizedEventsByDay[dayStr] || []).length && !timeOffByDay[dayStr]?.allDay && !(timeOffByDay[dayStr]?.ranges || []).length
  })

  const handleJournalOpen = (date) => { setIsJournalModalOpen(true); setSelectedDate(date) }
  const handleJournalClose = () => setIsJournalModalOpen(false)
  const handleBookingCancel = () => { setIsBookingModalOpen(false); setSelectedBookingId(null) }
  const handleBookingCancelSuccess = () => { handleBookingCancel(); refreshCalendarBookings() }

  return {
    isBookingModalOpen, setIsBookingModalOpen, selectedBookingId, setSelectedBookingId,
    selectedDate, isJournalModalOpen, isCompact, active,
    canViewJournal, canCreateJournal, canEditJournal, journalNoteDates,
    startHour, totalHeight, timeOffByDay, isWeekEmpty,
    normalizedEventsByDay, eventsByDayWithCols, gridTemplateColumns, gridMinWidth,
    getVisibleCount, showMore, showLess, MAX_VISIBLE,
    handleJournalOpen, handleJournalClose, handleBookingCancel, handleBookingCancelSuccess,
    refreshCalendarBookings,
  }
}

export default useWeekGridData
